import type { AdvertisementDto } from "@/api/advertising";
import {
  Button,
  MissingValue,
  StatusBadge,
  SubTable,
  Truncate,
  useToast,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { formatCalendarDate } from "@/design-system/lib/format";
import {
  AdvertisementStatusBadge,
  channelLabel,
  CostCell,
  formatPriceRange,
  money,
} from "./advertisingBits";

/** The absolute per-ad link — the team's landing page plus `?ad=<token>`. */
export function absoluteAdLink(linkPath: string): string {
  return `${window.location.origin}${linkPath}`;
}

interface Props {
  ads: AdvertisementDto[];
  /** For the caption. */
  teamName?: string | null;
  /** Whether the CALLER may edit these advertisements and record their spend —
   *  the scope endpoint's `canManageAds` AND the team's own `canManage`,
   *  resolved by the caller. The Edit control and the empty-state copy both
   *  read it; nothing here re-derives it from a permission list. */
  canManage: boolean;
  onSpend: (ad: AdvertisementDto) => void;
  onEdit: (ad: AdvertisementDto) => void;
}

/**
 * One team's advertisements, with what each cost and what it bought.
 *
 * <p>Rendered by the advertising page and by a team's ADVERTISING tab — the
 * SAME rows, columns and empty copy, so a manager reading their team's tab and
 * the global page cannot be shown two different answers for one advertisement.
 * Budgets and the cross-team roll-up are deliberately NOT here: they are the
 * global page's own, and the tab links across for them.</p>
 */
export function AdvertisementsTable({ ads, teamName, canManage, onSpend, onEdit }: Props) {
  const toast = useToast();

  /**
   * A copy affordance that tells the truth. The Clipboard API is absent on an
   * insecure origin — exactly where a staff member is most likely to be
   * testing — and a button that claimed success there would have them paste
   * whatever was on the clipboard before.
   */
  async function copyLink(a: AdvertisementDto) {
    if (!a.linkPath) return;
    const url = absoluteAdLink(a.linkPath);
    if (!navigator.clipboard?.writeText) {
      toast.error(`Copying is not available here. The link is ${url}`);
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Link copied for ${a.name}.`);
    } catch {
      toast.error(`Could not copy the link. It is ${url}`);
    }
  }

  const columns: SubTableColumn<AdvertisementDto>[] = [
    {
      key: "name",
      header: "Advertisement",
      render: (a) => (
        <div className="min-w-0">
          <Truncate className="font-medium text-onyx">{a.name}</Truncate>
          <div className="text-caption text-gray-mid">{channelLabel(a.channel)}</div>
          {/* Single line with the full copy on hover and focus (§11.1/§11.4) —
              `Truncate` carries the reveal; never a native `title`. */}
          {a.adCopy && (
            <Truncate className="block text-caption text-gray-mid">{a.adCopy}</Truncate>
          )}
        </div>
      ),
      sortAccessor: (a) => a.name,
      searchText: (a) => `${a.name} ${channelLabel(a.channel)} ${a.adCopy ?? ""}`,
    },
    {
      key: "link",
      header: "Link",
      render: (a) =>
        a.linkPath ? (
          <Button variant="text" size="sm" onClick={() => void copyLink(a)}>
            Copy link
          </Button>
        ) : (
          // An actionable absence in prose (§6.1, §7): the team has no site,
          // and that is set on the team, not on this row — so the row names
          // the state and offers no door it could not open.
          <span className="text-gray-mid italic">No site yet</span>
        ),
    },
    {
      key: "target",
      header: "Target",
      render: (a) => {
        const range = formatPriceRange(a.targetPriceMin, a.targetPriceMax);
        const place = [a.location, a.locationState].filter(Boolean).join(", ");
        if (!range && !place && !a.industry) return <MissingValue />;
        return (
          <div className="min-w-0 text-caption">
            {range && <div className="text-onyx">{range}</div>}
            {(a.industry || place) && (
              <Truncate className="block text-gray-mid">
                {[a.industry, place].filter(Boolean).join(" · ")}
              </Truncate>
            )}
          </div>
        );
      },
      searchText: (a) =>
        `${a.industry ?? ""} ${a.location ?? ""} ${a.locationState ?? ""}`,
    },
    {
      key: "status",
      header: "Status",
      render: (a) => <AdvertisementStatusBadge status={a.status} />,
      sortAccessor: (a) => a.status,
    },
    {
      key: "attribution",
      header: "Attribution",
      render: (a) =>
        a.hasNoAttributionSignal ? (
          // Not an error — an advertisement may legitimately be tracked for
          // spend alone — but a permanent zero in the lead column reads as
          // "this produced nothing", so the surface says which it is.
          <StatusBadge tone="warn">Not attributable</StatusBadge>
        ) : (
          <div className="min-w-0 text-caption">
            {a.partnerBrandName && <Truncate>{a.partnerBrandName}</Truncate>}
          </div>
        ),
    },
    {
      key: "dates",
      header: "Ran",
      render: (a) => (
        <span className="text-caption">
          {formatCalendarDate(a.startDate)}
          {a.endDate ? ` – ${formatCalendarDate(a.endDate)}` : ""}
        </span>
      ),
      sortAccessor: (a) => a.startDate,
    },
    {
      key: "spend",
      header: "Spend",
      align: "right",
      render: (a) => money(a.metrics.spendToDate),
      sortAccessor: (a) => a.metrics.spendToDate,
    },
    {
      key: "leads",
      header: "Leads",
      align: "right",
      render: (a) => a.metrics.leads,
      sortAccessor: (a) => a.metrics.leads,
    },
    {
      key: "cpl",
      header: "Cost / lead",
      align: "right",
      render: (a) => <CostCell value={a.metrics.costPerLead} />,
      sortAccessor: (a) => a.metrics.costPerLead ?? Number.MAX_SAFE_INTEGER,
    },
    {
      key: "qualified",
      header: "Qualified",
      align: "right",
      render: (a) => a.metrics.qualified,
      sortAccessor: (a) => a.metrics.qualified,
    },
    {
      key: "cpq",
      header: "Cost / qualified",
      align: "right",
      render: (a) => <CostCell value={a.metrics.costPerQualified} />,
    },
    {
      key: "deals",
      header: "Converted",
      align: "right",
      render: (a) => a.metrics.converted,
      sortAccessor: (a) => a.metrics.converted,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (a) => (
        <div className="flex justify-end gap-space-2">
          <Button variant="text" onClick={() => onSpend(a)}>Spend</Button>
          {canManage && (
            <Button variant="text" onClick={() => onEdit(a)}>
              Edit
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <SubTable
      caption={`Advertisements for ${teamName ?? "this team"}`}
      columns={columns}
      rows={ads}
      rowKey={(a) => a.id}
      empty={{
        message: canManage
          ? "No advertisements yet. Add one to start tracking what it costs and what it brings in."
          : "No advertisements yet.",
      }}
    />
  );
}
